import React, { useContext, useEffect, useMemo, useState } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { ApiContext } from '@api/api.tsx';
import { FindCarrierResponseOutDTO, FindCarrierTypeEnum } from '@api/ringil-api.v2.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ContractedCarrierForm, PriceTableForm } from '@components/ringil3/Features/Contracted/types.ts';
import CarrierPriceMatrix from '@components/ringil3/Features/Contracted/CarrierPriceMatrix.tsx';

/** Carriers of the customer with their users - same search the distribution selector uses, loaded once per mount. */
export const useContractedCarrierCandidates = (enabled = true) => {
  const { cuValidateCarriersForExpeditionsV3 } = useContext(ApiContext);
  const [candidates, setCandidates] = useState<FindCarrierResponseOutDTO[]>([]);
  useEffect(() => {
    if (!enabled || candidates.length > 0) return;
    // without expedition validations it lists every carrier of the customer, together with the carriers' users
    cuValidateCarriersForExpeditionsV3({ q: '', expeditionValidations: [] }, data => setCandidates(data ?? []));
  }, [enabled]);
  return candidates;
};

interface ComponentProps {
  value: ContractedCarrierForm;
  onChange: (value: ContractedCarrierForm) => void;
  table: PriceTableForm;
  candidates: FindCarrierResponseOutDTO[];
  /** Carriers already chosen elsewhere, not offered again (the current one always is). */
  excludedCarrierIds?: string[];
  carrierError?: string;
  highlight?: { levelKey: string; rowKeys: string[] } | null;
  onSetTable?: () => void;
  readOnly?: boolean;
  dataTestIdPrefix?: string;
}

/** "Nasmlouvaný dopravce": the carrier, its addressed users and its price matrix. */
const ContractedCarrierFields: React.FC<ComponentProps> = ({
  value,
  onChange,
  table,
  candidates,
  excludedCarrierIds = [],
  carrierError,
  highlight,
  onSetTable,
  readOnly = false,
  dataTestIdPrefix = 'contracted-carrier',
}) => {
  const { tr } = useTranslationLgs();

  const carrierOptions = useMemo(
    () =>
      candidates
        .filter(c => c.findCarrierType === FindCarrierTypeEnum.CARRIER)
        .filter(c => c.id === value.carrierId || !excludedCarrierIds.includes(c.id))
        .map(c => ({ id: c.id, name: c.name }))
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    [candidates, excludedCarrierIds, value.carrierId],
  );

  const userOptions = useMemo(
    () =>
      candidates
        .filter(c => c.findCarrierType === FindCarrierTypeEnum.CARRIER_USER && c.id === value.carrierId)
        .flatMap(c => (c.userIds ?? []).map(userId => ({ id: userId, fullName: c.name })))
        .sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? '')),
    [candidates, value.carrierId],
  );

  // a carrier the candidates do not list any more (or not loaded yet) still shows by its stored name
  const options = value.carrierId && !carrierOptions.some(o => o.id === value.carrierId) ? [{ id: value.carrierId, name: value.carrierName }, ...carrierOptions] : carrierOptions;
  const users = value.users.length && !userOptions.length ? value.users : userOptions;

  return (
    <div className={'flex flex-col gap-4'}>
      <Field label={tr('ContractedCarriers.carrier', 'Dopravce')} required errorMessage={carrierError}>
        <Dropdown
          value={value.carrierId}
          options={options}
          optionLabel={'name'}
          optionValue={'id'}
          filter
          disabled={readOnly}
          placeholder={tr('ContractedCarriers.selectCarrier', 'vyberte')}
          onChange={e => {
            const carrier = options.find(c => c.id === e.value);
            onChange({ ...value, carrierId: e.value, carrierName: carrier?.name ?? null, users: [] });
          }}
          style={{ width: '100%' }}
          data-testid={`${dataTestIdPrefix}-select`}
        />
      </Field>

      {value.carrierId && (
        <Field
          label={tr('ContractedCarriers.addressedUsers', 'Oslovení uživatelé')}
          helpText={tr('ContractedCarriers.addressedUsersHelp', 'Pokud nevyberete nikoho, poptávka bude zaslána všem uživatelům dopravce.')}
        >
          <MultiSelect
            value={value.users.map(u => u.id)}
            options={users}
            optionLabel={'fullName'}
            optionValue={'id'}
            filter
            display={'chip'}
            disabled={readOnly}
            placeholder={tr('ContractedCarriers.addContact', 'Přidat kontakt')}
            onChange={e => {
              const ids: string[] = e.value ?? [];
              onChange({ ...value, users: users.filter(u => ids.includes(u.id)) });
            }}
            className={'w-full'}
          />
        </Field>
      )}

      <div className={'flex flex-col gap-2'}>
        <div className={'flex flex-row items-center justify-between'}>
          <div className={'font-medium text-base'}>{tr('ContractedCarriers.contractedPrices', 'Smluvní ceny')}</div>
          {!readOnly && onSetTable && (
            <RingilButton mode={'text-only'} label={tr('ContractedCarriers.setTable', 'Nastavit tabulku')} onClick={onSetTable} dataTestId={`${dataTestIdPrefix}-set-table`} />
          )}
        </div>
        <CarrierPriceMatrix
          table={table}
          prices={value.prices}
          onChange={prices => onChange({ ...value, prices })}
          readOnly={readOnly}
          highlight={highlight}
          dataTestIdPrefix={`${dataTestIdPrefix}-matrix`}
        />
      </div>
    </div>
  );
};

export default ContractedCarrierFields;
